/* CRM functional enhancements — ExportMenu, DocumentsTab, RelationshipsPanel,
   BFSI inline props, DealAlertChips, CompanyAnalytics, DuplicateGuard helper.
   Pure ADDITIONS — no existing visual structures change. */

const { useState: useStateF, useEffect: useEffectF, useRef: useRefF, useMemo: useMemoF } = React;

/* ---------- helpers ---------- */
const _fmtMoney = (n) => '$' + (n >= 1000000 ? (n/1000000).toFixed(1) + 'M' : n >= 1000 ? (n/1000).toFixed(0) + 'K' : Math.round(n||0));
const _fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-IN') : '';
const _stripHtml = (s) => s ? String(s).replace(/<[^>]*>/g, '').replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&quot;/g,'"').replace(/&#39;/g,"'").trim() : '';
const _safeCell = (v) => { const s = String(v ?? ''); return /^[=+\-@]/.test(s) ? "'" + s : s; };
const _toCSV = (rows, columns) => {
  const head = columns.map(c => c.label).join(',');
  const body = rows.map(r => columns.map(c => {
    const v = c.get ? c.get(r) : r[c.key];
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }).join(',')).join('\n');
  return head + '\n' + body;
};
const _download = (filename, mime, content) => {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 500);
};
const _toExcelXML = (sheetName, rows, columns) => {
  // Minimal SpreadsheetML — opens cleanly in Excel/Sheets/Numbers
  const xml = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  const cell = (v) => {
    if (typeof v === 'number' && isFinite(v)) return `<Cell><Data ss:Type="Number">${v}</Data></Cell>`;
    return `<Cell><Data ss:Type="String">${xml(v ?? '')}</Data></Cell>`;
  };
  const headRow = `<Row>${columns.map(c => cell(c.label)).join('')}</Row>`;
  const bodyRows = rows.map(r => `<Row>${columns.map(c => cell(c.get ? c.get(r) : r[c.key])).join('')}</Row>`).join('');
  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="${xml(sheetName)}"><Table>${headRow}${bodyRows}</Table></Worksheet>
</Workbook>`;
};
const _printPDF = (title, rows, columns) => {
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return false;
  const xml = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const headHtml = columns.map(c => `<th>${xml(c.label)}</th>`).join('');
  const bodyHtml = rows.map(r => `<tr>${columns.map(c => `<td>${xml(c.get ? c.get(r) : r[c.key])}</td>`).join('')}</tr>`).join('');
  w.document.write(`<!doctype html><html><head><title>${xml(title)}</title>
    <style>
      *{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Inter,sans-serif;}
      body{margin:28px;color:#0F172A;}
      .hd{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #125AB0;padding-bottom:12px;margin-bottom:18px;}
      .hd h1{margin:0;font-size:20px;letter-spacing:-0.01em;}
      .hd .meta{font-size:11px;color:#64748b;}
      table{width:100%;border-collapse:collapse;font-size:11px;}
      th{text-align:left;background:#F1F4F8;color:#64748b;font-weight:700;padding:8px 10px;border-bottom:1px solid #E5EAF2;text-transform:uppercase;letter-spacing:0.03em;font-size:10px;}
      td{padding:8px 10px;border-bottom:1px solid #F1F4F8;}
      tr:hover td{background:#FAFBFD;}
      .foot{margin-top:20px;font-size:10px;color:#94a3b8;text-align:center;}
    </style></head>
    <body>
      <div class="hd"><h1>${xml(title)}</h1><div class="meta">Generated ${new Date().toLocaleString()} · ${rows.length} records</div></div>
      <table><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
      <div class="foot">${xml(window.__nx_auth?.user?.activeOrgName || 'CRM')} · Confidential</div>
      <script>setTimeout(()=>window.print(),300);<\/script>
    </body></html>`);
  w.document.close();
  return true;
};

/* Standard export column presets — CRM fields only.
   Active Custom Field definitions are appended dynamically at export time.
   BFSI-specific model fields (loanType, creditScore, etc.) are intentionally
   excluded: they are domain-specific and null for non-BFSI orgs. */
const EXPORT_COLUMNS = {
  people: [
    { key: 'salutation', label: 'Salutation' },
    { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' },
    { key: 'role', label: 'Role' }, { key: 'company', label: 'Company' }, { key: 'status', label: 'Status' },
    { key: 'score', label: 'Score' }, { key: 'owner', label: 'Owner' }, { key: 'city', label: 'City' },
    { key: 'tags', label: 'Tags', get: (p) => (p.tags || []).join(', ') },
    { key: 'lastActivity', label: 'Last Activity' },
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'createdAt', label: 'Created Date', get: (p) => _fmtDate(p.createdAt) },
    { key: 'updatedAt', label: 'Last Modified', get: (p) => _fmtDate(p.updatedAt) },
    { key: 'sourceSystem', label: 'Source System', get: (p) => p.sourceSystem || 'Manual' },
  ],
  companies: [
    { key: 'name', label: 'Name' }, { key: 'domain', label: 'Domain' }, { key: 'industry', label: 'Industry' },
    { key: 'employees', label: 'Employees' }, { key: 'revenue', label: 'Revenue' }, { key: 'location', label: 'Location' },
    { key: 'lifecycle', label: 'Lifecycle' }, { key: 'score', label: 'Score' }, { key: 'owner', label: 'Owner' },
    { key: 'deals', label: 'Open Deals' },
    { key: 'tags', label: 'Tags', get: (c) => (c.tags || []).join(', ') },
    { key: 'lastActivityAt', label: 'Last Activity' },
  ],
  deals: [
    { key: 'title', label: 'Deal' },
    { key: 'company', label: 'Company', get: (d) => d.company?.name ?? '' },
    { key: 'value', label: 'Value' },
    { key: 'stage', label: 'Stage', get: (d) => d.stage?.name ?? '' },
    { key: 'priority', label: 'Priority' },
    { key: 'owner', label: 'Owner', get: (d) => d.owner?.name ?? '' },
    { key: 'expectedCloseDate', label: 'Close Date' },
    { key: 'status', label: 'Status' },
    { key: 'tags', label: 'Tags', get: (d) => (d.tags || []).join(', ') },
  ],
  tasks: [
    { key: 'title', label: 'Title' }, { key: 'description', label: 'Description' },
    { key: 'status', label: 'Status' }, { key: 'priority', label: 'Priority' },
    { key: 'assignee', label: 'Assignee', get: (t) => t.assignee?.name ?? '' },
    { key: 'dueDate', label: 'Due Date' },
  ],
};
window.EXPORT_COLUMNS = EXPORT_COLUMNS;

/* Shared async helper: build standard + active CF columns, batch-fetching any missing values.
   Exposed on window so BulkBar handlers in records.jsx can reuse the same logic without duplication. */
const _prepareCfExport = async (type, rows, store) => {
  const standardCols = EXPORT_COLUMNS[type] || [];
  const cfDefs = (store.customFieldDefs?.[type] || [])
    .filter(d => d.status === 'ACTIVE')
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  if (!cfDefs.length) return { cols: standardCols, hadCfError: false };

  const existing = store.cfValues?.[type] || {};
  const missingIds = rows.map(r => r.id).filter(id => !existing[id]);
  let cfMap = { ...existing };
  let hadCfError = false;

  if (missingIds.length) {
    const wsId = store.workspaceId;
    const BATCH = 200;
    const batches = [];
    for (let i = 0; i < missingIds.length; i += BATCH) batches.push(missingIds.slice(i, i + BATCH));
    try {
      const results = await Promise.all(
        batches.map(b => window.__nx_api.customFields.getBatchValues(wsId, type, b))
      );
      for (const res of results) { if (res?.values) Object.assign(cfMap, res.values); }
    } catch (_) {
      hadCfError = true; /* CF fetch failed — export proceeds with empty CF cells */
    }
  }

  return {
    cols: [
      ...standardCols,
      ...cfDefs.map(d => ({ label: d.label, get: (r) => cfMap[r.id]?.[d.id] ?? '' })),
    ],
    hadCfError,
  };
};
window._prepareCfExport = _prepareCfExport;
window._toCSV = _toCSV;
window._download = _download;

/* Complete export — multi-sheet XLSX using authoritative backend data (no store pageSize limit).
   Sheets: People, Deals, Activities, Notes, Tasks. */
const _completeExportXLSX = async (type, rows, store) => {
  const wsId = store.workspaceId;
  const personIds = rows.map(r => r.id);
  const payload = await window.__nx_api.people.exportComplete(wsId, {
    personIds,
    includeDeals: true,
    includeActivities: true,
    includeNotes: true,
    includeTasks: true,
    includeEmails: true,
    includeMeetings: true,
    includeFiles: true,
    includeDiscuss: true,
  });

  const { people: ppl, deals, activities, notes, tasks, emails, meetings, files, discuss } = payload;

  const { cols: peopleCols, hadCfError } = await _prepareCfExport('people', rows, store);

  const fmtPeopleCols = peopleCols.map(c => ({ ...c, get: c.get || ((r) => r[c.key]) }));
  const peopleRows = ppl.map(p =>
    fmtPeopleCols.map(c => _safeCell(c.get ? c.get(p) : p[c.key]))
  );

  const dealCols = ['Person', 'Title', 'Company', 'Value', 'Currency', 'Stage', 'Owner', 'Priority', 'Probability', 'Expected Close', 'Status', 'Tags', 'Lost Reason', 'Created', 'Updated'];
  const dealRows = deals.map(d => [
    d.personId, d.title, d.company?.name ?? '', d.value ?? '', d.currency ?? '', d.stage?.name ?? '',
    d.owner?.name ?? '', d.priority ?? '', d.probability ?? '', _fmtDate(d.expectedCloseDate),
    d.status ?? '', (d.tags || []).join(', '), _safeCell(d.lostReason ?? ''),
    _fmtDate(d.createdAt), _fmtDate(d.updatedAt),
  ]);

  const actCols = ['Person', 'Type', 'Actor', 'Description', 'Detail', 'Company ID', 'Created'];
  const actRows = activities.map(a => [
    a.personId, a.type ?? '', _safeCell(a.actor ?? ''), _safeCell(a.description ?? ''),
    _safeCell(a.detail ?? ''), a.companyId ?? '', _fmtDate(a.createdAt),
  ]);

  const noteCols = ['Person', 'Title', 'Body', 'Author', 'Created', 'Updated'];
  const noteRows = notes.map(n => [
    n.personId, _safeCell(n.title ?? ''), _safeCell(_stripHtml(n.body ?? '')),
    _safeCell(n.author ?? ''), _fmtDate(n.createdAt), _fmtDate(n.updatedAt),
  ]);

  const taskCols = ['Person', 'Title', 'Description', 'Status', 'Priority', 'Assignee', 'Due Date', 'Completed At', 'Created', 'Updated'];
  const taskRows = tasks.map(t => [
    t.personId, _safeCell(t.title ?? ''), _safeCell(t.description ?? ''),
    t.status ?? '', t.priority ?? '', t.assignee?.name ?? '',
    _fmtDate(t.dueDate), _fmtDate(t.completedAt),
    _fmtDate(t.createdAt), _fmtDate(t.updatedAt),
  ]);

  const emailCols = ['Person', 'Subject', 'Type', 'Status', 'From Name', 'From Email', 'To', 'Body', 'Created'];
  const emailRows = (emails || []).map(e => [
    e.personId ?? '', _safeCell(e.subject ?? ''), e.type ?? '', e.status ?? '',
    _safeCell(e.fromName ?? ''), _safeCell(e.fromEmail ?? ''),
    (e.toEmails || []).join('; '), _safeCell(_stripHtml(e.body ?? '')),
    _fmtDate(e.createdAt),
  ]);

  const meetingCols = ['Person', 'Title', 'Status', 'Scheduled At', 'Duration (min)', 'Location', 'Agenda', 'Notes', 'Outcomes', 'Next Actions', 'Completed At', 'Created'];
  const meetingRows = (meetings || []).map(m => [
    m.personId ?? '', _safeCell(m.title ?? ''), m.status ?? '',
    _fmtDate(m.scheduledAt), m.duration ?? '', _safeCell(m.location ?? ''),
    _safeCell(m.agenda ?? ''), _safeCell(m.notes ?? ''),
    _safeCell(m.outcomes ?? ''), _safeCell(m.nextActions ?? ''),
    _fmtDate(m.completedAt), _fmtDate(m.createdAt),
  ]);

  const fileCols = ['Person', 'File Name', 'MIME Type', 'File Size', 'Uploaded By', 'Created At'];
  const fileRows = (files || []).map(f => [
    f.personId ?? '', _safeCell(f.fileName ?? ''), f.mimeType ?? '',
    f.fileSize ?? '', _safeCell(f.uploadedBy ?? ''), _fmtDate(f.createdAt),
  ]);

  const discussCols = ['Person', 'Comment', 'Author', 'Is Reply', 'Created', 'Modified'];
  const discussRows = (discuss || []).map(c => [
    c.recordId ?? '', _safeCell(_stripHtml(c.body ?? '')), _safeCell(c.authorName ?? ''),
    c.parentId != null ? 'Yes' : 'No', _fmtDate(c.createdAt), _fmtDate(c.updatedAt),
  ]);

  const XL = window.XLSX;
  const wb = XL.utils.book_new();

  const addSheet = (name, header, dataRows) => {
    const ws = XL.utils.aoa_to_sheet([header, ...dataRows]);
    ws['!cols'] = header.map(() => ({ wch: 20 }));
    XL.utils.book_append_sheet(wb, ws, name.slice(0, 31));
  };

  addSheet('People', fmtPeopleCols.map(c => c.label), peopleRows);
  addSheet('Deals', dealCols, dealRows);
  addSheet('Activities', actCols, actRows);
  addSheet('Notes', noteCols, noteRows);
  addSheet('Tasks', taskCols, taskRows);
  addSheet('Emails', emailCols, emailRows);
  addSheet('Meetings', meetingCols, meetingRows);
  addSheet('Files', fileCols, fileRows);
  addSheet('Discuss', discussCols, discussRows);

  const stamp = new Date().toISOString().slice(0, 10);
  XL.writeFile(wb, `people-complete-${stamp}.xlsx`);

  return { totalPeople: ppl.length, hadCfError };
};
window._completeExportXLSX = _completeExportXLSX;

/* Pure-JS ZIP builder — stored mode (no compression), no external dependencies.
   Produces a valid PKZIP archive containing any number of text files. */
const _buildZip = (files) => {
  const enc = new TextEncoder();
  // CRC-32 lookup table (IEEE polynomial)
  const crcTbl = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    crcTbl[n] = c;
  }
  const crc32 = (buf) => {
    let c = 0xFFFFFFFF;
    for (let i = 0; i < buf.length; i++) c = crcTbl[(c ^ buf[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
  };
  const u16 = (n) => new Uint8Array([n & 0xFF, (n >> 8) & 0xFF]);
  const u32 = (n) => new Uint8Array([n & 0xFF, (n >> 8) & 0xFF, (n >> 16) & 0xFF, (n >> 24) & 0xFF]);
  const cat = (...arrs) => {
    const out = new Uint8Array(arrs.reduce((s, a) => s + a.length, 0));
    let p = 0;
    arrs.forEach(a => { out.set(a, p); p += a.length; });
    return out;
  };
  const parts = [];
  const cdParts = [];
  let offset = 0;
  for (const { name, content } of files) {
    const nb = enc.encode(name);
    const cb = typeof content === 'string' ? enc.encode(content) : content;
    const crc = crc32(cb);
    const sz = cb.length;
    const lfh = cat(
      new Uint8Array([0x50,0x4B,0x03,0x04]),
      u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(sz), u32(sz),
      u16(nb.length), u16(0), nb,
    );
    parts.push(lfh, cb);
    cdParts.push(cat(
      new Uint8Array([0x50,0x4B,0x01,0x02]),
      u16(20), u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(sz), u32(sz),
      u16(nb.length), u16(0), u16(0), u16(0), u16(0), u32(0),
      u32(offset), nb,
    ));
    offset += lfh.length + sz;
  }
  const cdSize = cdParts.reduce((s, c) => s + c.length, 0);
  const eocd = cat(
    new Uint8Array([0x50,0x4B,0x05,0x06]),
    u16(0), u16(0),
    u16(files.length), u16(files.length),
    u32(cdSize), u32(offset), u16(0),
  );
  return cat(...parts, ...cdParts, eocd);
};

/* Complete export — ONE ZIP package containing People.csv, Deals.csv, Activities.csv,
   Notes.csv, Tasks.csv. Single user action → single download. */
const _completeExportCSV = async (type, rows, store) => {
  const wsId = store.workspaceId;
  const personIds = rows.map(r => r.id);
  const payload = await window.__nx_api.people.exportComplete(wsId, {
    personIds,
    includeDeals: true,
    includeActivities: true,
    includeNotes: true,
    includeTasks: true,
    includeEmails: true,
    includeMeetings: true,
    includeFiles: true,
    includeDiscuss: true,
  });

  const { people: ppl, deals, activities, notes, tasks, emails, meetings, files, discuss } = payload;
  const { cols: peopleCols, hadCfError } = await _prepareCfExport('people', rows, store);

  const csvOf = (header, dataRows) => {
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g,'""') + '"' : s; };
    return [header.join(','), ...dataRows.map(r => r.map(esc).join(','))].join('\n');
  };

  const fmtPeopleCols = peopleCols.map(c => ({ ...c, get: c.get || ((r) => r[c.key]) }));
  const peopleRows = ppl.map(p => fmtPeopleCols.map(c => _safeCell(c.get ? c.get(p) : p[c.key])));

  const dealCols = ['Person','Title','Company','Value','Currency','Stage','Owner','Priority','Probability','Expected Close','Status','Tags','Lost Reason','Created','Updated'];
  const dealRows = deals.map(d => [d.personId, d.title, d.company?.name ?? '', d.value ?? '', d.currency ?? '', d.stage?.name ?? '', d.owner?.name ?? '', d.priority ?? '', d.probability ?? '', _fmtDate(d.expectedCloseDate), d.status ?? '', (d.tags||[]).join(', '), _safeCell(d.lostReason ?? ''), _fmtDate(d.createdAt), _fmtDate(d.updatedAt)]);

  const actCols = ['Person','Type','Actor','Description','Detail','Company ID','Created'];
  const actRows = activities.map(a => [a.personId, a.type ?? '', _safeCell(a.actor ?? ''), _safeCell(a.description ?? ''), _safeCell(a.detail ?? ''), a.companyId ?? '', _fmtDate(a.createdAt)]);

  const noteCols = ['Person','Title','Body','Author','Created','Updated'];
  const noteRows = notes.map(n => [n.personId, _safeCell(n.title ?? ''), _safeCell(_stripHtml(n.body ?? '')), _safeCell(n.author ?? ''), _fmtDate(n.createdAt), _fmtDate(n.updatedAt)]);

  const taskCols = ['Person','Title','Description','Status','Priority','Assignee','Due Date','Completed At','Created','Updated'];
  const taskRows = tasks.map(t => [t.personId, _safeCell(t.title ?? ''), _safeCell(t.description ?? ''), t.status ?? '', t.priority ?? '', t.assignee?.name ?? '', _fmtDate(t.dueDate), _fmtDate(t.completedAt), _fmtDate(t.createdAt), _fmtDate(t.updatedAt)]);

  const emailCols = ['Person','Subject','Type','Status','From Name','From Email','To','Body','Created'];
  const emailRows = (emails || []).map(e => [e.personId ?? '', _safeCell(e.subject ?? ''), e.type ?? '', e.status ?? '', _safeCell(e.fromName ?? ''), _safeCell(e.fromEmail ?? ''), (e.toEmails||[]).join('; '), _safeCell(_stripHtml(e.body ?? '')), _fmtDate(e.createdAt)]);

  const meetingCols = ['Person','Title','Status','Scheduled At','Duration (min)','Location','Agenda','Notes','Outcomes','Next Actions','Completed At','Created'];
  const meetingRows = (meetings || []).map(m => [m.personId ?? '', _safeCell(m.title ?? ''), m.status ?? '', _fmtDate(m.scheduledAt), m.duration ?? '', _safeCell(m.location ?? ''), _safeCell(m.agenda ?? ''), _safeCell(m.notes ?? ''), _safeCell(m.outcomes ?? ''), _safeCell(m.nextActions ?? ''), _fmtDate(m.completedAt), _fmtDate(m.createdAt)]);

  const fileCols = ['Person','File Name','MIME Type','File Size','Uploaded By','Created At'];
  const fileRows = (files || []).map(f => [f.personId ?? '', _safeCell(f.fileName ?? ''), f.mimeType ?? '', f.fileSize ?? '', _safeCell(f.uploadedBy ?? ''), _fmtDate(f.createdAt)]);

  const discussCols = ['Person','Comment','Author','Is Reply','Created','Modified'];
  const discussRows = (discuss || []).map(c => [c.recordId ?? '', _safeCell(_stripHtml(c.body ?? '')), _safeCell(c.authorName ?? ''), c.parentId != null ? 'Yes' : 'No', _fmtDate(c.createdAt), _fmtDate(c.updatedAt)]);

  const zipBytes = _buildZip([
    { name: 'People.csv',     content: csvOf(fmtPeopleCols.map(c => c.label), peopleRows) },
    { name: 'Deals.csv',      content: csvOf(dealCols, dealRows) },
    { name: 'Activities.csv', content: csvOf(actCols, actRows) },
    { name: 'Notes.csv',      content: csvOf(noteCols, noteRows) },
    { name: 'Tasks.csv',      content: csvOf(taskCols, taskRows) },
    { name: 'Emails.csv',     content: csvOf(emailCols, emailRows) },
    { name: 'Meetings.csv',   content: csvOf(meetingCols, meetingRows) },
    { name: 'Files.csv',      content: csvOf(fileCols, fileRows) },
    { name: 'Discuss.csv',    content: csvOf(discussCols, discussRows) },
  ]);

  const stamp = new Date().toISOString().slice(0, 10);
  _download(`people-complete-${stamp}.zip`, 'application/zip', zipBytes);

  return { totalPeople: ppl.length, hadCfError };
};

/* ---------- ExportMenu ---------- */
const ExportMenu = ({ type, rows, label = 'Export' }) => {
  const [open, setOpen] = useStateF(false);
  const [exporting, setExporting] = useStateF(false);
  const [completeExporting, setCompleteExporting] = useStateF(false);
  const ref = useRefF();
  const toast = useToast();
  const store = useStore();
  useEffectF(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handle = async (fmt) => {
    setOpen(false);
    if (!rows.length) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing To Export', desc: 'Adjust your filters and try again.' });
      return;
    }

    setExporting(true);
    const { cols, hadCfError } = await _prepareCfExport(type, rows, store);
    setExporting(false);

    const stamp = new Date().toISOString().slice(0, 10);
    const base = type + '-' + stamp;

    if (fmt === 'csv') {
      _download(base + '.csv', 'text/csv;charset=utf-8;', _toCSV(rows, cols));
    } else if (fmt === 'excel') {
      _download(base + '.xls', 'application/vnd.ms-excel;charset=utf-8;', _toExcelXML(type, rows, cols));
    } else if (fmt === 'pdf') {
      const ok = _printPDF(type.charAt(0).toUpperCase() + type.slice(1) + ' Export', rows, cols);
      if (!ok) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Popup blocked', desc: 'Allow pop-ups to export PDF.' }); return; }
    }
    if (hadCfError) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Exported ' + rows.length + ' rows', desc: 'Custom field values could not be loaded and were left blank.' });
    } else {
      toast({ tone: 'success', icon: 'File', title: 'Exported ' + rows.length + ' rows', desc: fmt.toUpperCase() });
    }
  };

  const handleComplete = async (fmt) => {
    setOpen(false);
    if (!rows.length) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Nothing To Export', desc: 'Adjust your filters and try again.' });
      return;
    }
    setCompleteExporting(true);
    try {
      let result;
      if (fmt === 'xlsx') {
        result = await _completeExportXLSX(type, rows, store);
        const desc = result.hadCfError
          ? 'Custom field values could not be loaded and were left blank.'
          : `${result.totalPeople} people with Deals, Activities, Notes & Tasks`;
        toast({ tone: result.hadCfError ? 'warning' : 'success', icon: 'File', title: 'Complete Export ready', desc });
      } else if (fmt === 'csv') {
        result = await _completeExportCSV(type, rows, store);
        const desc = result.hadCfError
          ? 'Custom field values could not be loaded and were left blank.'
          : `${result.totalPeople} people · 5 CSV files downloaded`;
        toast({ tone: result.hadCfError ? 'warning' : 'success', icon: 'File', title: 'Complete Export ready', desc });
      }
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Complete Export failed', desc: err?.message || 'An unexpected error occurred. Please try again.' });
    } finally {
      setCompleteExporting(false);
    }
  };

  const isAnyExporting = exporting || completeExporting;

  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <Btn variant="secondary" icon="Download" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} disabled={isAnyExporting}>
        {completeExporting ? 'Building export…' : exporting ? 'Preparing…' : label}
      </Btn>
      {open && (
        <div className="nx-chip-menu" role="menu" style={{ right: 0, left: 'auto', top: 'calc(100% + 6px)', minWidth: 210 }}>
          <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => handle('csv')}>
            <NexusIcon name="FileText" size={12} /><span>Export as CSV</span><span className="nx-muted nx-mini" style={{ marginLeft: 'auto' }}>{rows.length}</span>
          </button>
          <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => handle('excel')}>
            <NexusIcon name="FileSpreadsheet" size={12} /><span>Export as Excel</span>
          </button>
          <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => handle('pdf')}>
            <NexusIcon name="Printer" size={12} /><span>Export as PDF</span>
          </button>
          {type === 'people' && (
            <>
              <div style={{ borderTop: '1px solid var(--nx-border)', margin: '4px 0' }} />
              <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => handleComplete('xlsx')}>
                <NexusIcon name="Package" size={12} /><span>Complete Export (XLSX)</span>
              </button>
              <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => handleComplete('csv')}>
                <NexusIcon name="Archive" size={12} /><span>Complete Export (CSV)</span>
              </button>
            </>
          )}
        </div>
      )}
    </span>
  );
};
window.ExportMenu = ExportMenu;

/* ---------- BFSI inline-edit props ---------- */
const BFSIPersonProps = ({ record }) => {
  const store = useStore();
  const update = (field, value) => store.update('people', record.id, { [field]: value });
  const B = window.NEXUS_BFSI;
  return (
    <div className="nx-prop-grid">
      <SelectProp label="Loan Type" value={record.loanType || '—'} options={B.loanTypes} onSave={v => update('loanType', v)} />
      <EditableProp label="Credit Score" value={record.creditScore ?? '—'} mono numeric onSave={v => update('creditScore', Number(v) || 0)} />
      <SelectProp label="Employment" value={record.employmentType || '—'} options={B.employmentTypes} onSave={v => update('employmentType', v)} />
      <EditableProp label="Income" value={record.income || '—'} mono onSave={v => update('income', v)} />
      <SelectProp label="Repayment" value={record.repaymentStatus || '—'} options={B.repaymentStates} onSave={v => update('repaymentStatus', v)} pill={record.repaymentStatus === 'On Time' ? 'green' : record.repaymentStatus === 'Defaulted' ? 'red' : 'amber'} />
      <SelectProp label="Risk Level" value={record.riskLevel || '—'} options={B.riskLevels} onSave={v => update('riskLevel', v)} pill={record.riskLevel === 'Low' ? 'green' : record.riskLevel === 'High' ? 'red' : 'amber'} />
    </div>
  );
};
window.BFSIPersonProps = BFSIPersonProps;

const BFSIDealProps = ({ record }) => {
  const store = useStore();
  const update = (field, value) => store.update('deals', record.id, { [field]: value });
  const B = window.NEXUS_BFSI;
  return (
    <div className="nx-prop-grid">
      <EditableProp label="Loan Amount" value={record.loanAmount || 0} mono prefix="$" numeric onSave={v => update('loanAmount', Number(v) || 0)} />
      <EditableProp label="ROI %" value={record.roi || 0} mono numeric onSave={v => update('roi', Number(v) || 0)} />
      <EditableProp label="Tenure" value={record.tenure || '—'} mono onSave={v => update('tenure', v)} />
      <EditableProp label="Sanction" value={record.sanctionAmount || 0} mono prefix="$" numeric onSave={v => update('sanctionAmount', Number(v) || 0)} />
      <EditableProp label="Disb. Date" value={record.disbursementDate || '—'} mono onSave={v => update('disbursementDate', v)} />
      <EditableProp label="EMI" value={record.emi || 0} mono prefix="$" numeric onSave={v => update('emi', Number(v) || 0)} />
      <SelectProp label="Repay Status" value={record.repaymentStatus || '—'} options={B.repaymentStates} onSave={v => update('repaymentStatus', v)} pill={record.repaymentStatus === 'On Time' ? 'green' : record.repaymentStatus === 'Overdue' ? 'red' : 'amber'} />
    </div>
  );
};
window.BFSIDealProps = BFSIDealProps;

/* ---------- DealAlertChips ---------- */
const DealAlertChips = ({ alerts, size = 'sm' }) => {
  if (!alerts || !alerts.length) return null;
  const cat = window.NEXUS_BFSI.alertCatalog;
  return (
    <span className="nx-alert-chips" data-size={size}>
      {alerts.map(a => {
        const meta = cat[a]; if (!meta) return null;
        return (
          <span key={a} className="nx-alert-chip" style={{ color: meta.tone, borderColor: meta.tone + '40', background: meta.tone + '14' }} title={meta.label}>
            <NexusIcon name={meta.icon} size={10} />
            {size !== 'xs' && <span>{meta.label}</span>}
          </span>
        );
      })}
    </span>
  );
};
window.DealAlertChips = DealAlertChips;

/* ---------- Company analytics panel ---------- */
const CompanyAnalyticsPanel = ({ company }) => {
  const store = useStore();
  const deals = store.deals.filter(d => d.companyId === company.id);
  const pipeline = store.pipeline || [];
  const isTerminal = (d) => {
    const stg = pipeline.find(s => s.id === d.stageId);
    return stg?.kind === 'WON' || stg?.kind === 'LOST';
  };
  const active = deals.filter(d => !isTerminal(d));
  const won    = deals.filter(d => pipeline.find(s => s.id === d.stageId)?.kind === 'WON');
  const pendingCollections = active.reduce((s, d) => s + (d.value || 0), 0);
  const totalValue = deals.reduce((s, d) => s + d.value, 0);
  const companyPeople = store.people.filter(p => p.companyId === company.id);
  const repayOnTime = companyPeople.filter(p => p.repaymentStatus === 'On Time').length;
  const repayPct = companyPeople.length ? Math.round((repayOnTime / companyPeople.length) * 100) : 0;
  const stats = [
    { k: 'Total Deals',         v: deals.length,                  sub: _fmtMoney(totalValue) },
    { k: 'Active Loans',        v: active.length,                 sub: _fmtMoney(active.reduce((s,d)=>s+d.value,0)) },
    { k: 'Active Pipeline',     v: _fmtMoney(pendingCollections), sub: active.length + ' accounts' },
    { k: 'Repayment On-Time',   v: repayPct + '%',                sub: repayOnTime + '/' + companyPeople.length },
  ];
  // Deal activity trend — real deal count per month for last 6 months
  const now = new Date();
  const trend = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const m = d.getMonth(), y = d.getFullYear();
    return deals.filter(dl => {
      if (!dl.createdAt) return false;
      const dd = new Date(dl.createdAt);
      return dd.getMonth() === m && dd.getFullYear() === y;
    }).length;
  });
  const max = Math.max(...trend, 1);
  return (
    <div className="nx-company-anal">
      <div className="nx-company-anal-stats">
        {stats.map(s => (
          <div key={s.k} className="nx-company-anal-stat">
            <span className="nx-prop-label">{s.k}</span>
            <span className="nx-company-anal-val">{s.v}</span>
            <span className="nx-muted nx-mini">{s.sub}</span>
          </div>
        ))}
      </div>
      <div className="nx-company-anal-trend">
        <div className="nx-prop-label" style={{ marginBottom: 6 }}>Deal Activity (6 mo)</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 56 }}>
          {trend.map((v, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ width: '100%', height: (v / max) * 48, background: 'linear-gradient(180deg, #125AB0, #1E72D6)', borderRadius: '4px 4px 0 0', transition: 'height 0.4s ease' }} title={v + ' deal' + (v !== 1 ? 's' : '')} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
window.CompanyAnalyticsPanel = CompanyAnalyticsPanel;

/* ---------- RelationshipsPanel ---------- */
const RelationshipsPanel = ({ record, type }) => {
  const store = useStore();
  const _stages = store.pipeline || [];
  const isPerson  = type === 'people';
  const isCompany = type === 'companies';
  const isDeal    = type === 'deals';

  const linkedCompanies = isPerson ? store.companies.filter(c => c.id === record.companyId)
                        : isDeal   ? store.companies.filter(c => c.id === record.companyId)
                        : [];
  const linkedPeople    = isCompany ? store.people.filter(p => p.companyId === record.id)
                        : isDeal    ? store.people.filter(p => p.id === record.contactId)
                        : [];
  const linkedDeals     = isPerson  ? store.deals.filter(d => d.contactId === record.id)
                        : isCompany ? store.deals.filter(d => d.companyId === record.id)
                        : [];
  const linkedTasks = store.tasks.filter(t =>
    isPerson  ? t.personId  === record.id :
    isCompany ? t.companyId === record.id :
    isDeal    ? t.dealId    === record.id :
    false
  );

  const Row = ({ icon, label, count, onClick }) => (
    <button className="nx-rel-row" onClick={onClick}>
      <NexusIcon name={icon} size={12} />
      <span className="nx-rel-label">{label}</span>
      <span className="nx-rel-count">{count}</span>
      <NexusIcon name="ArrowRight" size={11} className="nx-rel-go" />
    </button>
  );

  return (
    <div className="nx-rel-stack">
      {linkedCompanies.map(c => (
        <button key={c.id} className="nx-rel-record" onClick={() => store.openDrawer(c)}>
          <span className="nx-domain-mark" style={{ width: 26, height: 26, fontSize: 12 }}>{c.name[0]}</span>
          <div className="nx-rel-record-body">
            <span className="nx-rel-record-title">{c.name}</span>
            <span className="nx-muted nx-mini">{c.industry} · {c.location}</span>
          </div>
          <NexusIcon name="ChevronRight" size={11} />
        </button>
      ))}
      {linkedPeople.slice(0, 4).map(p => (
        <button key={p.id} className="nx-rel-record" onClick={() => store.openDrawer(p)}>
          <Avatar name={p.name} tone={p.tone} size={26} />
          <div className="nx-rel-record-body">
            <span className="nx-rel-record-title">{p.name}</span>
            <span className="nx-muted nx-mini">{p.role}</span>
          </div>
          <NexusIcon name="ChevronRight" size={11} />
        </button>
      ))}
      {linkedDeals.slice(0, 4).map(d => {
        const stage = _stages.find(s => s.id === d.stageId);
        return (
          <button key={d.id} className="nx-rel-record" onClick={() => store.openDrawer(d)}>
            <span className="nx-deal-hero-icon" style={{ width: 26, height: 26 }}><NexusIcon name="Target" size={12} /></span>
            <div className="nx-rel-record-body">
              <span className="nx-rel-record-title">{d.title}</span>
              <span className="nx-muted nx-mini">{stage?.name} · {_fmtMoney(d.value)}</span>
            </div>
            <DealAlertChips alerts={d.alerts} size="xs" />
            <NexusIcon name="ChevronRight" size={11} />
          </button>
        );
      })}
      {linkedTasks.length > 0 && (
        <div className="nx-rel-section">
          <div className="nx-prop-label" style={{ marginBottom: 6 }}>Linked Tasks ({linkedTasks.length})</div>
          {linkedTasks.slice(0, 4).map(t => (
            <div key={t.id} className="nx-mini-task">
              <span className={'nx-check' + (t.done ? ' is-done' : '')} />
              <span className="nx-task-title">{t.title}</span>
              <span className="nx-task-due">{t.dueDate}</span>
            </div>
          ))}
        </div>
      )}
      {linkedCompanies.length + linkedPeople.length + linkedDeals.length + linkedTasks.length === 0 && (
        <span className="nx-muted nx-mini" style={{ padding: 8 }}>No linked records yet.</span>
      )}
    </div>
  );
};
window.RelationshipsPanel = RelationshipsPanel;

/* ---------- DocumentsTab ---------- */
const _FILE_TYPES  = ['PDF', 'Image', 'Sheet', 'Doc', 'Other'];
const _TYPE_ICON   = { PDF: 'FileText', Image: 'File', Sheet: 'FileSpreadsheet', Doc: 'FileText', Other: 'File' };
const _TYPE_COLOR  = { PDF: '#E24329', Image: '#0EA5E9', Sheet: '#16A34A', Doc: '#2563EB', Other: '#64748b' };

const _docFileType = (d) => {
  const mime = (d.type || '').toLowerCase();
  const name = (d.name || '').toLowerCase();
  if (mime.includes('pdf')         || name.endsWith('.pdf'))                                                   return 'PDF';
  if (mime.startsWith('image/')    || /\.(png|jpg|jpeg|gif|webp|svg|bmp)$/.test(name))                        return 'Image';
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv') || /\.(xls|xlsx|csv)$/.test(name)) return 'Sheet';
  if (mime.includes('word')        || mime.includes('document') || mime.includes('text/plain') || /\.(doc|docx|txt|rtf|odt)$/.test(name)) return 'Doc';
  return 'Other';
};

const DocumentsTab = ({ record, type, onCountChange }) => {
  const store = useStore();
  const toast = useToast();
  const fileRef = useRefF(null);
  const [docs,       setDocs]       = useStateF(null);
  const [fetchErr,   setFetchErr]   = useStateF(null);
  const [uploading,  setUploading]  = useStateF(false);
  const [filterType, setFilterType] = useStateF(null);

  const wsId = store.workspaceId;

  const _buildParam = () =>
    type === 'people'    ? { personId:  record.id } :
    type === 'companies' ? { companyId: record.id } :
    type === 'tasks'     ? { taskId:    record.id } :
                           { dealId:    record.id };

  useEffectF(() => {
    if (!wsId) return;
    setDocs(null);
    setFetchErr(null);
    const param = _buildParam();
    window.__nx_api.document.list(wsId, { ...param, pageSize: 100 })
      .then(res => {
        const raw = Array.isArray(res) ? res : (res.data || res.items || []);
        setDocs(raw.map(d => window.__nx_api.enums.normDocument(d)));
        onCountChange?.(raw.length);
      })
      .catch(err => setFetchErr(err?.message || 'Failed to load files'));
  }, [wsId, record.id, type]);

  const doRefetch = () => {
    if (!wsId) return;
    const param = _buildParam();
    window.__nx_api.document.list(wsId, { ...param, pageSize: 100 })
      .then(res => {
        const raw = Array.isArray(res) ? res : (res.data || res.items || []);
        setDocs(raw.map(d => window.__nx_api.enums.normDocument(d)));
        onCountChange?.(raw.length);
      })
      .catch(() => toast({ tone: 'error', icon: 'AlertCircle', title: 'Refresh failed' }));
  };

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    if (!wsId) { toast({ tone: 'error', icon: 'AlertCircle', title: 'No active workspace' }); return; }
    setUploading(true);
    let uploaded = 0;
    const meta = _buildParam();
    for (const f of files) {
      try {
        await window.__nx_api.document.upload(wsId, f, meta);
        uploaded++;
      } catch (err) {
        toast({ tone: 'error', icon: 'AlertCircle', title: 'Upload failed', desc: f.name + (err?.message ? ': ' + err.message : '') });
      }
    }
    e.target.value = '';
    setUploading(false);
    if (uploaded > 0) {
      toast({ tone: 'success', icon: 'File', title: uploaded + ' file' + (uploaded > 1 ? 's' : '') + ' uploaded' });
      doRefetch();
    }
  };

  const onDelete = (id) => {
    store.confirm({
      title: 'Delete this document?',
      description: 'This file will be permanently removed from the record.',
      danger: true,
      onConfirm: () => {
        window.__nx_api.document.remove(wsId, id)
          .then(() => {
            setDocs(prev => prev ? prev.filter(d => d.id !== id) : prev);
            toast({ tone: 'success', icon: 'Trash', title: 'Document deleted' });
          })
          .catch(err => toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: err?.message || 'Could not delete file' }));
      },
    });
  };

  /* Loading skeleton */
  if (docs === null && !fetchErr) {
    return (
      <div className="nx-drawer-page nx-docs-page">
        <div className="nx-docs-upload">
          <span className="nx-docs-upload-hint">Loading files…</span>
          <Btn variant="primary" icon="Upload" disabled>Upload File</Btn>
        </div>
        <div className="nx-docs-skeleton">
          {[0, 1, 2].map(i => (
            <div key={i} className="nx-docs-skeleton-card">
              <div className="nx-skeleton-line" style={{ width: '65%' }} />
              <div className="nx-skeleton-line" style={{ width: '40%' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* Error state */
  if (fetchErr) {
    return (
      <div className="nx-drawer-page nx-docs-page">
        <div className="nx-empty">
          <NexusIcon name="AlertCircle" size={20} />
          <span>{fetchErr}</span>
          <button className="nx-link-btn" onClick={() => { setFetchErr(null); setDocs(null); const param = _buildParam(); window.__nx_api.document.list(wsId, { ...param, pageSize: 100 }).then(res => { const raw = Array.isArray(res) ? res : (res.data || res.items || []); setDocs(raw.map(d => window.__nx_api.enums.normDocument(d))); }).catch(err => setFetchErr(err?.message || 'Failed to load files')); }}>Retry</button>
        </div>
      </div>
    );
  }

  const typeCounts = _FILE_TYPES.reduce((acc, t) => { acc[t] = docs.filter(d => _docFileType(d) === t).length; return acc; }, {});
  const visible = filterType ? docs.filter(d => _docFileType(d) === filterType) : docs;

  return (
    <div className="nx-drawer-page nx-docs-page">
      <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.docx,.doc,.txt,.csv,.zip" style={{ display: 'none' }} onChange={onUpload} />

      <div className="nx-docs-upload">
        <span className="nx-docs-upload-hint">{docs.length} file{docs.length !== 1 ? 's' : ''} attached</span>
        <Btn variant="primary" icon="Upload" disabled={uploading} onClick={onPick}>
          {uploading ? 'Uploading…' : 'Upload File'}
        </Btn>
      </div>

      {docs.length > 0 && (
        <div className="nx-docs-filters">
          <button className={'nx-docs-filter' + (!filterType ? ' is-active' : '')} onClick={() => setFilterType(null)}>
            All <span className="nx-rel-count">{docs.length}</span>
          </button>
          {_FILE_TYPES.filter(t => typeCounts[t] > 0).map(t => (
            <button key={t} className={'nx-docs-filter' + (filterType === t ? ' is-active' : '')}
              onClick={() => setFilterType(filterType === t ? null : t)}
              style={{ '--dot': _TYPE_COLOR[t] }}>
              <span className="nx-doc-dot" />{t} <span className="nx-rel-count">{typeCounts[t]}</span>
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="nx-empty">
          <NexusIcon name="File" size={20} />
          <span>{docs.length === 0 ? 'No files yet. Click Upload File to attach one.' : 'No ' + filterType + ' files attached.'}</span>
        </div>
      ) : (
        <div className="nx-files-grid">
          {visible.map(d => {
            const ft = _docFileType(d);
            const ftl = ft.toLowerCase();
            return (
              <div key={d.id} className="nx-file nx-doc-file">
                <span className={'nx-file-icon nx-doc-icon--' + ftl}>
                  <NexusIcon name={_TYPE_ICON[ft]} size={20} />
                </span>
                <span className={'nx-doc-type-badge nx-doc-type-badge--' + ftl}>{ft}</span>
                <span className="nx-file-name" title={d.name}>{d.name}</span>
                <span className="nx-file-meta">{d.size}{(d.uploadedAt || d.uploaded) ? ' · ' + (d.uploadedAt || d.uploaded) : ''}</span>
                <div className="nx-doc-actions">
                  <button className="nx-iconbtn" title="Preview" onClick={async () => {
                    if (!wsId) return;
                    try {
                      const url = await window.__nx_api.document.download(wsId, d.id, d.name, true);
                      window.open(url, '_blank');
                    } catch {
                      toast({ tone: 'error', icon: 'AlertCircle', title: 'Preview failed', desc: d.name });
                    }
                  }}><NexusIcon name="Eye" size={14} /></button>
                  <button className="nx-iconbtn" title="Download" onClick={async () => {
                    if (!wsId) return;
                    try {
                      await window.__nx_api.document.download(wsId, d.id, d.name);
                    } catch {
                      toast({ tone: 'error', icon: 'AlertCircle', title: 'Download failed', desc: d.name });
                    }
                  }}><NexusIcon name="Download" size={14} /></button>
                  <button className="nx-iconbtn nx-iconbtn--danger" title="Delete" onClick={() => onDelete(d.id)}>
                    <NexusIcon name="Trash" size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
window.DocumentsTab = DocumentsTab;
